import { UseQueryOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"

import {
  TFetchTaskTemplates,
  TTaskTemplateFetchFilters,
} from "src/data/taskManagement/taskManagementTypes"
import {
  taskTemplateKeys,
  useTaskTemplateCache,
} from "src/data/taskManagement/taskTemplateQueriesCache"
import {
  useMinutDelete,
  useMinutPatch,
  useMinutPost,
  useMinutQuery,
} from "src/hooks/minutQuery"

export function usePostTaskTemplate() {
  const taskCache = useTaskTemplateCache()

  return useMinutPost<"/organizations/{organization_id}/tasks/templates", 403>({
    pathFn: (path) => `/organizations/${path.organization_id}/tasks/templates`,
    options: {
      onSuccess: () => {
        return taskCache.invalidateTaskTemplateList()
      },
    },
  })
}

export function useFetchTaskTemplates<Select = TFetchTaskTemplates>({
  orgId,
  filters,
  options,
}: {
  orgId: string
  filters?: TTaskTemplateFetchFilters
  options?: UseQueryOptions<TFetchTaskTemplates, AxiosError, Select, string[]>
}) {
  return useMinutQuery<
    `/organizations/{organization_id}/tasks/templates`,
    403,
    Select
  >({
    apiPath: "/organizations/{organization_id}/tasks/templates",
    queryPath: `/organizations/${orgId}/tasks/templates`,
    queryKey: taskTemplateKeys.taskTemplateList(filters),
    filters,
    options: {
      enabled: options?.enabled,
    },
  })
}

export function usePatchTaskTemplate() {
  const cache = useTaskTemplateCache()

  return useMinutPatch<
    "/organizations/{organization_id}/tasks/templates/{task_template_id}",
    403
  >({
    pathFn: (path) =>
      `/organizations/${path.organization_id}/tasks/templates/${path.task_template_id}`,
    options: {
      onSuccess: () => {
        return cache.invalidateTaskTemplateList()
      },
    },
  })
}

export function useDeleteTaskTemplate() {
  const cache = useTaskTemplateCache()

  return useMinutDelete<
    "/organizations/{organization_id}/tasks/templates/{task_template_id}",
    400 | 404
  >({
    pathFn: (path) =>
      `/organizations/${path.organization_id}/tasks/templates/${path.task_template_id}`,
    options: {
      onSuccess: () => {
        return cache.invalidateTaskTemplateList()
      },
    },
  })
}
